import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import { Divider, Typography } from "antd";
import { useSelector } from "react-redux";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartLoader from "./ChartLoader";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const { Title: AntTitle } = Typography;

const PaidConversationChart = () => {
  const { whatsappBusinessInsightsLoading, WhatsappBusinessPricingInsights } =
    useSelector((store) => store.Channel);

  const chartData = useMemo(() => {
    if (!WhatsappBusinessPricingInsights?.length) return null;

    // Group data by date and conversation category for REGULAR type only
    const groupedData = WhatsappBusinessPricingInsights.reduce((acc, item) => {
      if (item.pricing_type !== "REGULAR") return acc;

      const date = new Date(item.start * 1000).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
      const category = item.pricing_category.toLowerCase();

      if (!acc[date]) {
        acc[date] = {
          marketing: 0,
          marketing_lite: 0,
          utility: 0,
          authentication: 0,
          authentication_international: 0
        };
      }

      acc[date][category] += item.volume;
      return acc;
    }, {});

    // Sort dates
    const sortedDates = Object.keys(groupedData).sort((a, b) => {
      return new Date(a) - new Date(b);
    });

    return {
      labels: ["", ...sortedDates],
      datasets: [
        {
          label: "Marketing",
          data: [null, ...sortedDates.map((date) => groupedData[date].marketing)],
          borderColor: "#9748ff",
          backgroundColor: "#9748ff",
          borderWidth: 1.5,
          tension: 0.4,
        },
        {
          label: "Marketing Lite",
          data: [null, ...sortedDates.map((date) => groupedData[date].marketing_lite)],
          borderColor: "#05805d",
          backgroundColor: "#05805d",
          borderWidth: 1.5,
          tension: 0.4,
        },
        {
          label: "Utility",
          data: [null, ...sortedDates.map((date) => groupedData[date].utility)],
          borderColor: "#f6a722",
          backgroundColor: "#f6a722",
          borderWidth: 1.5,
          tension: 0.4,
        },
        {
          label: "Authentication",
          data: [null, ...sortedDates.map((date) => groupedData[date].authentication)],
          borderColor: "#00d998",
          backgroundColor: "#00d998",
          borderWidth: 1.5,
          tension: 0.4,
        },
        {
          label: "Authentication International",
          data: [null, ...sortedDates.map((date) => groupedData[date].authentication_international)],
          borderColor: "#1578f2",
          backgroundColor: "#1578f2",
          borderWidth: 1.5,
          tension: 0.4,
        },
      ],
    };
  }, [WhatsappBusinessPricingInsights]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          // callback: (value) => `${Number(value) / 1000}K`,
          // stepSize: 1000,
          // max: 5000,
        },
      },
    },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 20,
          boxWidth: 8,
          boxHeight: 8,
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        position: "nearest",
        callbacks: {
          footer: function (tooltipItems) {
            // Sum all values in the tooltipItems array
            const total = tooltipItems.reduce(
              (sum, item) => sum + (item.raw || 0),
              0,
            );
            return `Total Conversations: ${total}`;
          },
        },
      },
    },
  };

  const EMPTY_CHART_DATA = {
    labels: [],
    datasets: [
      {
        label: "Marketing",
        data: [],
        borderColor: "#9748ff",
        backgroundColor: "#9748ff",
        borderWidth: 1.5,
        tension: 0.4,
      },
      {
        label: "Utility",
        data: [],
        borderColor: "#f6a722",
        backgroundColor: "#f6a722",
        borderWidth: 1.5,
        tension: 0.4,
      },
      {
        label: "Authentication",
        data: [],
        borderColor: "#1578f2",
        backgroundColor: "#1578f2",
        borderWidth: 1.5,
        tension: 0.4,
      },
      {
        label: "Service",
        data: [],
        borderColor: "#00d998",
        backgroundColor: "#00d998",
        borderWidth: 1.5,
        tension: 0.4,
      },
    ],
  };

  return (
    <div className="w-full p-4 bg-white rounded-lg">
      {/* <AntTitle level={5} className="text-gray-700 mb-4">
        Paid Conversations
      </AntTitle>
      <Divider className="mt-0" /> */}
      <div className="w-[60vw] mx-auto h-96 relative">
        {whatsappBusinessInsightsLoading ? (
          <ChartLoader />
        ) : (
          <>
            <Line data={chartData || EMPTY_CHART_DATA} options={options} />
            {(!chartData ||
              !chartData.labels ||
              chartData.labels.length === 0) && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-white bg-opacity-80">
                <span className="text-gray-500 text-lg font-medium">
                  No data available
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PaidConversationChart;
